import { getBackWeightFields, BackWeightData, BackWeightFields } from '@/library/BackWeightHelper';
import { BackWeightDropdown } from './BackWeightDropdown';
import { useState } from 'react';
import { useActivePlayer } from '@/context/ActivePlayerContext';
import Modal from '@/common/Modal';
import Tile from '@/common/Tile';
import EditableTile from '@/common/EditableTile';
import { TourBagIronDetail } from '@/api/models';
import { calculateFinalHeadWeight } from './caluclateFinalHeadWeight';
import styles from './FinalHeadWeightModal.module.scss';

const sanitize = (value: string) => {
  return value.replace(/[^0-9.]/g, ''); // keep only digits + dot
};

export type IFinalHeadWeightIron = Pick<TourBagIronDetail, 'tourBagIronDetailId' | 'headWeight' | 'drill' | 'tipPlug' | 'glueWeight' | 'leadTape'> & BackWeightFields;

interface IFinalHeadWeightModalProps {
  showFinalHWModal: boolean;
  closeFinalHWModal: () => void;
  iron: TourBagIronDetail;
  // eslint-disable-next-line no-unused-vars
  handleUpdate?: (iron: IFinalHeadWeightIron) => void;
  isEditMode?: boolean;
  backWeightData?: BackWeightData;
}

function FinalHeadWeightModal({ showFinalHWModal, closeFinalHWModal, iron, handleUpdate, backWeightData, isEditMode = false }: IFinalHeadWeightModalProps) {
  const { player } = useActivePlayer();
  const [backWeight, setBackWeight] = useState<BackWeightFields>(() => getBackWeightFields(iron));
  const [headWeight, setHeadWeight] = useState(iron.headWeight || '');
  const [drill, setDrill] = useState(iron.drill || '');
  const [tipPlug, setTipPlug] = useState(iron.tipPlug || '');
  const [glueWeight, setGlueWeight] = useState(iron.glueWeight || '');
  const [leadTape, setLeadTape] = useState(iron.leadTape || '');
  const finalHeadWeight = calculateFinalHeadWeight({ headWeight, drill, tipPlug, glueWeight, leadTape });

  const onUpdate = async () => {
    try {
      const partialIron = {
        ...backWeight,
        tourBagIronDetailId: iron.tourBagIronDetailId,
        headWeight: Number(headWeight),
        drill: Number(drill),
        tipPlug: Number(tipPlug),
        glueWeight: Number(glueWeight),
        leadTape: Number(leadTape),
      };
      handleUpdate?.(partialIron);
      closeFinalHWModal();
    } catch (error) {
      console.error('Failed to Edit Iron final head weight: ', error);
      // TODO: Save error to ErrorLogRepo
      throw error;
    }
  };

  const onCancel = () => {
    setBackWeight(getBackWeightFields(iron));
    setHeadWeight(iron.headWeight || '');
    setDrill(iron.drill || '');
    setTipPlug(iron.tipPlug || '');
    setGlueWeight(iron.glueWeight || '');
    setLeadTape(iron.leadTape || '');
    closeFinalHWModal();
  };

  return (
    <Modal isOpen={showFinalHWModal} onClose={closeFinalHWModal} className={`${styles.modal} ${isEditMode ? styles.whiteBackground : styles.grayBackground}`}>
      <div className={styles.container}>
        <div className={styles.title}>Final Head Weight</div>
        <div className={styles.info}>
          {player?.fullName} - {iron.familyName} {iron.headModelName}
        </div>
        <div className={styles.tiles}>
          {!isEditMode && (
            <>
              <Tile label="HW" className={styles.finalHWTile} value={headWeight?.toString() || '---'} />
              <Tile label="Back Wt" className={styles.finalHWTile} value={backWeight.headWeightNumberBackLookupName || '---'} />
              <Tile label="Drill" className={styles.finalHWTile} value={drill?.toString() || '---'} />
              <Tile label="Tip Plug" className={styles.finalHWTile} value={tipPlug?.toString() || '---'} />
              <Tile label="Glue" className={styles.finalHWTile} value={glueWeight?.toString() || '---'} />
              <Tile label="Lead Tape" className={styles.finalHWTile} value={leadTape?.toString() || '---'} />
              <Tile label="Final HW" className={styles.finalHWTile} value={finalHeadWeight?.toString() === '0' ? '---' : finalHeadWeight?.toString()} />
            </>
          )}
          {isEditMode && (
            <div className={styles.editableTiles}>
              <EditableTile label="HW" className={styles.finalHWTile} value={headWeight?.toString() || '---'} onChange={(newValue) => setHeadWeight(sanitize(newValue))} />
              {backWeightData && <BackWeightDropdown headModelId={iron.headModelId} value={backWeight} data={backWeightData} onChange={setBackWeight} className={styles.finalHWTile} />}
              <EditableTile label="Drill" className={styles.finalHWTile} value={drill?.toString() || '---'} onChange={(newValue) => setDrill(sanitize(newValue))} />
              <EditableTile label="Tip Plug" className={styles.finalHWTile} value={tipPlug?.toString() || '---'} onChange={(newValue) => setTipPlug(sanitize(newValue))} />
              <EditableTile label="Glue" className={styles.finalHWTile} value={glueWeight?.toString() || '---'} onChange={(newValue) => setGlueWeight(sanitize(newValue))} />
              <EditableTile label="Lead Tape" className={styles.finalHWTile} value={leadTape?.toString() || '---'} onChange={(newValue) => setLeadTape(sanitize(newValue))} />
              <Tile label="Final HW" className={styles.finalHWTile} value={finalHeadWeight?.toString() === '0' ? '---' : finalHeadWeight?.toString()} />
            </div>
          )}
        </div>
        <div className={styles.buttons}>
          {isEditMode && (
            <>
              <button className={styles.update} onClick={onUpdate}>
                Update
              </button>
              <button className={styles.close} onClick={onCancel}>
                Cancel
              </button>
            </>
          )}
          {!isEditMode && (
            <>
              <button className={styles.close} onClick={closeFinalHWModal}>
                Close
              </button>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}

export default FinalHeadWeightModal;
